import { Navigate } from "react-router-dom";

import { UserRoleEnum } from "@/api/user/models";
import { useDepartmentRoute } from "@/hooks/common/use-department-route";
import { RoutePathEnum } from "@/routes/paths";
import { useUserStore } from "@/stores/use-user-store";
import { getUserInitialDepartmentRoute } from "@/utils/department-routes";
import { getUserRoleByDepartment } from "@/utils/user-positions";

interface RoleGuardProps {
  /**
   * Lista de roles permitidas para acessar esta rota.
   * Se a role do usuário não estiver nesta lista, será redirecionado.
   */
  allowedRoles: UserRoleEnum[];
  /**
   * Conteúdo a ser renderizado se o acesso for permitido.
   */
  children: React.ReactNode;
}

/**
 * Componente que protege rotas exclusivas de roles específicas.
 *
 * Valida se a role do usuário autenticado está na lista de roles permitidas.
 * Se não estiver, redireciona para a rota inicial do department do usuário.
 *
 * @example
 * // Rota exclusiva para administradores e coordenadores
 * {
 *   path: 'colaboradores',
 *   element: (
 *     <RoleGuard allowedRoles={[UserRoleEnum.ADMINISTRATOR, UserRoleEnum.COORDINATOR]}>
 *       <CollaboratorsPage />
 *     </RoleGuard>
 *   )
 * }
 */
export function RoleGuard({ allowedRoles, children }: RoleGuardProps) {
  const activeUser = useUserStore((state) => state.activeUser);
  const { department } = useDepartmentRoute();
  const activeRole = getUserRoleByDepartment(activeUser, department);

  if (!activeUser || !activeRole || !allowedRoles.includes(activeRole)) {
    const redirectTo = activeUser ? getUserInitialDepartmentRoute(activeUser) : RoutePathEnum.LOGIN;
    return <Navigate to={redirectTo ?? RoutePathEnum.LOGIN} replace />;
  }

  return <>{children}</>;
}
